import { createSlice } from "@reduxjs/toolkit";
import type { JobTypeName } from "@tradingdroid/frontends-types";

import type { JsonValue } from "@src/types";

import type { PayloadAction } from "@reduxjs/toolkit";

type ActionType =
  | "success"
  | "danger"
  | "warning"
  | "info"
  | "primary"
  | "secondary"
  | "light"
  | "dark";

export interface ToastAction {
  /** React key. Falls back to the label, so set it when two actions share one. */
  id?: string;
  label: string;
  onClick: () => void;
  color?: ActionType;
}

export interface Toast {
  id: string;
  title?: string;
  message: string;
  type?: ActionType;
  timeout?: number;
  actions?: ToastAction[];
}

export interface JsonModalAction {
  /** React key. Falls back to the label, so set it when two actions share one. */
  id?: string;
  label: string;
  onClick: (data: JsonValue) => void;
  type?: ActionType;
}

export interface JsonModalData {
  id: string;
  title?: string;
  data: Record<string, any>;
  actions?: JsonModalAction[];
}

/** Config for prompt input field (mimics window.prompt functionality) */
export interface ConfirmModalPromptConfig {
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  type?: "text" | "number";
  helpText?: string;
}

export interface ConfirmModalData {
  id: string;
  message: string;
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmColor?: ActionType;
  /** Optional prompt input (mimics window.prompt) */
  promptConfig?: ConfirmModalPromptConfig;
}

export interface JobPollingState {
  id: string;
  jobId: number;
  jobType: JobTypeName;
  status: "queued" | "claimed" | "running" | "completed" | "failed" | "cancelled" | "paused";
  entityType?: "backtest" | "optimization" | "execution";
  entityId?: number;
  startTime: number;
  lastChecked: number;
  metadata?: {
    title?: string;
    description?: string;
  } & Record<string, unknown>;
}

export interface LayoutState {
  toasts: Required<Toast>[];
  jsonModal: JsonModalData | null;
  confirmModal: ConfirmModalData | null;
  activeJobs: JobPollingState[];
  shouldSyncJobs: boolean; // Flag to trigger job sync
  pageTitle: string;
  isSyncing: boolean; // Incremental sync status
}

const initialState: LayoutState = {
  toasts: [],
  jsonModal: null,
  confirmModal: null,
  activeJobs: [],
  shouldSyncJobs: false,
  pageTitle: "TradingDroid",
  isSyncing: false,
};

const layoutSlice = createSlice({
  name: "layout",
  initialState,
  reducers: {
    addToast: (state, action: PayloadAction<Required<Toast>>) => {
      if (action.payload.type === "danger") console.error(action.payload.message);
      if (action.payload.type === "warning") console.warn(action.payload.message);
      state.toasts.push(action.payload);
    },
    removeToast: (state, action: PayloadAction<string>) => {
      state.toasts = state.toasts.filter((toast) => toast.id !== action.payload);
    },
    clearAllToasts: (state) => {
      state.toasts = [];
    },
    showJsonModal: (state, action: PayloadAction<Omit<JsonModalData, "id">>) => {
      state.jsonModal = {
        ...action.payload,
        id: Math.random().toString(36).substring(2, 9),
      };
    },
    hideJsonModal: (state) => {
      state.jsonModal = null;
    },
    showConfirmModal: (state, action: PayloadAction<Omit<ConfirmModalData, "id">>) => {
      state.confirmModal = {
        ...action.payload,
        id: Math.random().toString(36).substring(2, 9),
      };
    },
    hideConfirmModal: (state) => {
      state.confirmModal = null;
    },
    trackActiveJobAction: (
      state,
      action: PayloadAction<{
        jobId: number;
        jobType: JobPollingState["jobType"];
        entityType?: JobPollingState["entityType"];
        metadata?: JobPollingState["metadata"];
      }>,
    ) => {
      // Tracking a job already tracked changes nothing, except a paused one restarts as queued.
      const existing = state.activeJobs.find((job) => job.jobId === action.payload.jobId);
      if (existing) {
        if (existing.status === "paused") {
          existing.status = "queued";
          existing.lastChecked = Date.now();
        }
        return;
      }
      const newJob: JobPollingState = {
        id: Math.random().toString(36).substring(2, 9),
        jobId: action.payload.jobId,
        jobType: action.payload.jobType,
        entityType: action.payload.entityType,
        status: "queued",
        startTime: Date.now(),
        lastChecked: Date.now(),
        metadata: action.payload.metadata,
      };
      state.activeJobs.push(newJob);
    },
    updateActiveJobAction: (
      state,
      action: PayloadAction<{
        jobId: number;
        status: JobPollingState["status"];
        entityId?: number;
      }>,
    ) => {
      const job = state.activeJobs.find((j) => j.jobId === action.payload.jobId);
      if (job) {
        job.status = action.payload.status;
        job.lastChecked = Date.now();
        if (action.payload.entityId) {
          job.entityId = action.payload.entityId;
        }
      }
    },
    removeActiveJobAction: (state, action: PayloadAction<number>) => {
      state.activeJobs = state.activeJobs.filter((job) => job.jobId !== action.payload);
    },
    clearCompletedActiveJobs: (state) => {
      // A paused job is not over, but nothing polls it until a resume tracks it again.
      state.activeJobs = state.activeJobs.filter(
        (job) => !["completed", "failed", "cancelled", "paused"].includes(job.status),
      );
    },
    triggerJobSync: (state) => {
      // Set flag to trigger active jobs sync in JobPollingManager
      state.shouldSyncJobs = true;
    },
    resetJobSyncFlagAction: (state) => {
      // Reset the job sync flag
      state.shouldSyncJobs = false;
    },
    setPageTitle: (state, action: PayloadAction<string>) => {
      state.pageTitle = action.payload;
    },
    clearPageTitle: (state) => {
      state.pageTitle = "";
    },
    setSyncing: (state, action: PayloadAction<boolean>) => {
      state.isSyncing = action.payload;
    },
    /**
     * Reset layout state to initial state
     * Used on logout to clear active jobs and other session-specific UI state
     */
    resetLayoutState: (_state) => {
      return initialState;
    },
  },
});

export const {
  addToast,
  removeToast,
  clearAllToasts,
  showJsonModal,
  hideJsonModal,
  showConfirmModal,
  hideConfirmModal,
  trackActiveJobAction,
  updateActiveJobAction,
  removeActiveJobAction,
  clearCompletedActiveJobs,
  triggerJobSync,
  resetJobSyncFlagAction,
  setPageTitle,
  clearPageTitle,
  setSyncing,
  resetLayoutState,
} = layoutSlice.actions;
export const layoutReducer = layoutSlice.reducer;
